import { useContext, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { PrepApi } from 'api/prep';
import { UserContext } from 'contexts/UserContext';
import { Badge } from 'shadcn/components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

function TypeBadge({ type }) {
    const isComplete = type === 'Complete';
    const badgeClass = isComplete
        ? 'bg-green-100 text-green-700 hover:bg-green-100'
        : 'bg-blue-100 text-blue-700 hover:bg-blue-100';
    return <Badge className={`${badgeClass} rounded-full font-bold`}>{type}</Badge>;
}

function AllLocationsTable({ onViewPallets }) {
    const { user } = useContext(UserContext);
    const prepApi = new PrepApi();

    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setIsLoading(true);
                const response = await prepApi.getPrepLocations({ facilityId: user.facilityID });
                if (response.status) {
                    setLocations(response.data.rows);
                } else {
                    setError('Failed to fetch prep locations.');
                }
            } catch (err) {
                setError(err.message || 'An unexpected error occurred.');
            } finally {
                setIsLoading(false);
            }
        };

        if (user?.facilityID) {
            fetchLocations();
        }
    }, [user?.facilityID]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-10">
                <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
                <p className="text-lg font-semibold text-red-600">Failed to load data</p>
                <p className="text-muted-foreground">{error}</p>
            </div>
        );
    }

    if (!isLoading && locations.length === 0) {
        return (
            <div className="text-center py-10">
                <p className="text-lg font-semibold">No Prep Locations Found</p>
                <p className="text-muted-foreground">There are no prep locations configured for this facility.</p>
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow className="hover:bg-transparent">
                    <TableHead className="text-gray-500">LOCATION</TableHead>
                    <TableHead className="text-gray-500">TYPE</TableHead>
                    <TableHead className="text-gray-500">PALLETS</TableHead>
                    <TableHead className="text-gray-500">TOTAL UNITS</TableHead>
                    <TableHead className="text-gray-500">ACTIONS</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {locations.map((row) => (
                    <TableRow key={row.locationName} className="border-b-gray-100">
                        <TableCell>{row.locationName}</TableCell>
                        <TableCell>
                            <TypeBadge type={row.locationType} />
                        </TableCell>
                        <TableCell>{row.totalPallets}</TableCell>
                        <TableCell>{row.totalUnits}</TableCell>
                        <TableCell>
                            <button
                                onClick={() => onViewPallets(row)}
                                className="text-blue-500 font-medium hover:underline cursor-pointer"
                            >
                                View Pallets
                            </button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default AllLocationsTable;
